import React, { useCallback, useEffect, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PublishIcon from "@mui/icons-material/Publish";
import SaveIcon from "@mui/icons-material/Save";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import DatamuseService from "../../../services/DatamuseService";
import Logger from "../../../services/Logger";
import { useAdminLanguage } from "../../../context/AdminLanguageContext";
import CustomSnackbar from "../../components/CustomSnackbar";
import StepPuzzleWordPanel from "../../pzl_common/board/StepPuzzleWordPanel";
import AdminWordWheelPackService from "../pack/AdminWordWheelPackService";
import WordWheelPublishDialog from "../tpl/WordWheelPublishDialog";
import {
    WORD_WHEEL_SEASON_NONE,
    WORD_WHEEL_SEASON_OPTIONS,
    buildPublishMetadata,
    formatSeasonComment,
} from "../wordWheelLevelUtils";
import AdminWordWheelBoardService from "./AdminWordWheelBoardService";
import { WORD_WHEEL_GRID_SIZE } from "./wordWheelGridConstants";

const REVIEWED_BOARD_PATH = "/admin/puzzle/word_wheel_board?activeStep=2";
const BATCH_SIZE = 10;
const PACK_STORAGE_KEY = "admin.word_wheel_board.quick_publish.pack";

const FALLBACK_PACK_CODES = [
    ...WORD_WHEEL_SEASON_OPTIONS,
    "classic",
    "hard_quest",
    "master",
];

function loadSelectedPack() {
    try {
        const stored = localStorage.getItem(PACK_STORAGE_KEY);
        if (stored === "all" || stored === WORD_WHEEL_SEASON_NONE) {
            return stored;
        }
        if (stored && /^[a-z0-9_]+$/i.test(stored)) {
            return stored;
        }
    } catch (error) {
        Logger.error("Error reading quick-publish pack from localStorage:", error);
    }
    return "all";
}

function packLabel(code) {
    if (!code) {
        return code;
    }
    return formatSeasonComment(code) || code;
}

function findCharacter(row, col, filledCoordinates) {
    if (!filledCoordinates) {
        return "";
    }
    for (const coord of filledCoordinates) {
        const matchingPosition = coord.positions?.find(
            (p) => p.row === row && p.col === col
        );
        if (matchingPosition?.value) {
            return matchingPosition.value.toUpperCase();
        }
    }
    return "";
}

function initializeSquare(filledCoordinates) {
    const size = WORD_WHEEL_GRID_SIZE;
    const newSquare = Array.from({ length: size }, () => Array.from({ length: size }, () => ""));
    for (let row = 0; row < size; row++) {
        for (let col = 0; col < size; col++) {
            newSquare[row][col] = findCharacter(row, col, filledCoordinates);
        }
    }
    return newSquare;
}

function parseWordList(puzzle) {
    if (!puzzle?.filledCoordinates) {
        return [];
    }
    try {
        return JSON.parse(puzzle.filledCoordinates).map((item) => item.text).filter(Boolean);
    } catch {
        return [];
    }
}

function QuickPublishPuzzleCard({ puzzle, panelRef, onRefresh, onNotify }) {
    const parsePuzzleGrid = useCallback(() => {
        if (!puzzle?.filledCoordinates) {
            return [];
        }
        try {
            return initializeSquare(JSON.parse(puzzle.filledCoordinates));
        } catch (err) {
            Logger.error("Error parsing filled coordinates:", err);
            return [];
        }
    }, [puzzle?.filledCoordinates]);

    return (
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", height: "100%" }}>
            <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mb: 1, px: 0.5 }}
                noWrap
                title={puzzle?.title || "Untitled"}
            >
                {puzzle?.title || "Untitled"}
            </Typography>
            <StepPuzzleWordPanel
                ref={panelRef}
                puzzle={puzzle}
                parsePuzzleGrid={parsePuzzleGrid}
                enableScatterLayout
                onRefresh={onRefresh}
                onNotify={onNotify}
            />
        </Box>
    );
}

export default function WordWheelQuickPublisher() {
    const navigate = useNavigate();
    const { language } = useAdminLanguage();
    const panelRefs = useRef([]);

    const [selectedPack, setSelectedPack] = useState(loadSelectedPack);
    const [packOptions, setPackOptions] = useState(FALLBACK_PACK_CODES);
    const [puzzles, setPuzzles] = useState([]);
    const [queueTotal, setQueueTotal] = useState(null);
    const [queueLoading, setQueueLoading] = useState(true);
    const [queueError, setQueueError] = useState(null);
    const [error, setError] = useState(null);
    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingPublish, setLoadingPublish] = useState(false);
    const [openPublishDialog, setOpenPublishDialog] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const goToReviewedList = useCallback(() => {
        navigate(REVIEWED_BOARD_PATH);
    }, [navigate]);

    const notify = useCallback((severity, message) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }, []);

    const handlePackChange = useCallback((value) => {
        setSelectedPack(value);
        try {
            localStorage.setItem(PACK_STORAGE_KEY, value);
        } catch (err) {
            Logger.error("Error saving quick-publish pack to localStorage:", err);
        }
    }, []);

    const loadBatch = useCallback(async (excludeIds = []) => {
        setQueueLoading(true);
        setQueueError(null);
        try {
            const skip = Array.isArray(excludeIds) ? excludeIds.filter(Boolean) : [];
            const { content, total } = await AdminWordWheelBoardService.fetchReviewedQueue({
                language,
                pageSize: BATCH_SIZE + skip.length + 5,
                excludeIds: skip,
                season: selectedPack,
            });
            setQueueTotal(total);
            const rows = (content || []).slice(0, BATCH_SIZE);
            if (rows.length === 0) {
                setPuzzles([]);
                return;
            }
            const fullTemplates = await Promise.all(
                rows.map((row) => AdminWordWheelBoardService.requestTemplate(row.id))
            );
            setPuzzles(fullTemplates.filter(Boolean));
        } catch (err) {
            Logger.error("Error loading reviewed word wheel batch:", err);
            setQueueError("Failed to load reviewed puzzles. Please try again.");
            setPuzzles([]);
        } finally {
            setQueueLoading(false);
        }
    }, [language, selectedPack]);

    useEffect(() => {
        let cancelled = false;
        AdminWordWheelPackService.list()
            .then((items) => {
                if (cancelled) {
                    return;
                }
                const codes = items.map((pack) => pack.code).filter(Boolean);
                if (codes.length > 0) {
                    setPackOptions(codes);
                }
            })
            .catch((err) => {
                Logger.error("Error loading word wheel packs for quick-publish:", err);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        loadBatch();
    }, [loadBatch]);

    const refreshPuzzle = useCallback(async (puzzleId) => {
        try {
            const updated = await AdminWordWheelBoardService.requestTemplate(puzzleId);
            setPuzzles((prev) => prev.map((puzzle) => (puzzle.id === puzzleId ? updated : puzzle)));
        } catch (err) {
            Logger.error("Error refreshing word wheel template:", err);
        }
    }, []);

    const saveAllLayouts = async ({ silent } = {}) => {
        const refs = panelRefs.current.slice(0, puzzles.length);
        const results = await Promise.all(
            refs.map((panel) => panel?.saveLayout?.({ silent: true }) ?? true)
        );
        if (results.some((ok) => ok === false)) {
            if (!silent) {
                notify("warning", "Some layouts could not be saved.");
            }
            return false;
        }
        if (!silent) {
            notify("success", "Layouts saved.");
        }
        return true;
    };

    const handleSave = async () => {
        setLoadingSave(true);
        try {
            await saveAllLayouts();
        } finally {
            setLoadingSave(false);
        }
    };

    const handlePublishClick = async () => {
        const saved = await saveAllLayouts({ silent: true });
        if (saved === false) {
            return;
        }
        setOpenPublishDialog(true);
    };

    const handlePublish = async (publishOptions) => {
        if (puzzles.length === 0) {
            return;
        }
        const originalCursor = document.body.style.cursor;
        document.body.style.cursor = "wait";
        setLoadingPublish(true);
        try {
            setError(null);
            const items = [];
            for (const puzzle of puzzles) {
                const wordList = parseWordList(puzzle);
                const wordScores = await DatamuseService.requestWordScores(wordList);
                const metadata = buildPublishMetadata(puzzle, wordScores, publishOptions);
                items.push({
                    id: puzzle.id,
                    comment: publishOptions.comment ?? "",
                    details: metadata.details,
                    isFree: publishOptions.isFree ?? false,
                });
            }
            const result = await AdminWordWheelBoardService.publishTemplates(items);
            if (result?.code === "FAILURE") {
                setError(result.message || "Failed to publish puzzles. Please try again.");
                return;
            }
            const succeeded = result?.succeeded ?? puzzles.length;
            const failed = result?.failed ?? 0;
            if (failed > 0) {
                const failureText = (result?.failures || [])
                    .map((entry) => entry.message)
                    .filter(Boolean)
                    .join("; ");
                notify(
                    "warning",
                    `Published ${succeeded} of ${puzzles.length}${failureText ? `: ${failureText}` : "."}`
                );
            } else {
                notify("success", `Published ${succeeded} puzzle${succeeded === 1 ? "" : "s"}. Loading the next batch…`);
            }
            const publishedIds = puzzles.map((puzzle) => puzzle.id);
            await loadBatch(publishedIds);
        } catch (err) {
            Logger.error("Error publishing word wheel batch:", err);
            setError("Failed to publish puzzles. Please try again.");
        } finally {
            document.body.style.cursor = originalCursor || "default";
            setLoadingPublish(false);
            setOpenPublishDialog(false);
        }
    };

    const batchCount = puzzles.length;
    const busy = queueLoading || loadingPublish;

    const packSelect = (
        <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="ww-quick-publish-pack-label">Pack</InputLabel>
            <Select
                labelId="ww-quick-publish-pack-label"
                label="Pack"
                value={selectedPack}
                onChange={(e) => handlePackChange(e.target.value)}
                disabled={busy}
            >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value={WORD_WHEEL_SEASON_NONE}>No pack</MenuItem>
                {packOptions.map((code) => (
                    <MenuItem key={code} value={code}>{packLabel(code)}</MenuItem>
                ))}
                {selectedPack !== "all"
                    && selectedPack !== WORD_WHEEL_SEASON_NONE
                    && !packOptions.includes(selectedPack) && (
                    <MenuItem value={selectedPack}>{packLabel(selectedPack)}</MenuItem>
                )}
            </Select>
        </FormControl>
    );

    const toolbar = (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                position: "sticky",
                top: 0,
                zIndex: 2,
                py: 1.5,
                mb: 2,
                backgroundColor: "background.paper",
                borderBottom: "1px solid",
                borderColor: "divider",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flexWrap: "wrap" }}>
                <IconButton onClick={goToReviewedList} aria-label="Back to Reviewed" sx={{ minWidth: 44, minHeight: 44 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#3BA666" }}>
                    Quick Publisher
                </Typography>
                {packSelect}
                {batchCount > 0 && (
                    <Chip
                        label={`${batchCount} in this batch`}
                        color="success"
                        variant="outlined"
                    />
                )}
                {queueTotal != null && (
                    <Chip
                        label={`${queueTotal} reviewed remaining`}
                        variant="outlined"
                    />
                )}
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                    variant="outlined"
                    startIcon={loadingSave ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={busy || loadingSave || batchCount === 0}
                    sx={{ minHeight: 44, minWidth: 120 }}
                >
                    Save
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={loadingPublish ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                    onClick={handlePublishClick}
                    disabled={busy || loadingSave || batchCount === 0}
                    sx={{ minHeight: 44, minWidth: 140 }}
                >
                    {`Publish (${batchCount})`}
                </Button>
            </Box>
        </Box>
    );

    if (queueError && puzzles.length === 0 && !queueLoading) {
        return (
            <Box sx={{ width: "100%" }}>
                {toolbar}
                <Alert severity="error" sx={{ mb: 2 }}>{queueError}</Alert>
                <Button variant="outlined" onClick={() => loadBatch()} sx={{ minHeight: 44 }}>
                    Try again
                </Button>
            </Box>
        );
    }

    if (queueLoading && puzzles.length === 0) {
        return (
            <Box sx={{ width: "100%" }}>
                {toolbar}
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 400,
                    p: 3
                }}>
                    <CircularProgress size={28} />
                    <Typography variant="h6" sx={{ ml: 2 }}>Loading reviewed puzzles…</Typography>
                </Box>
            </Box>
        );
    }

    if (!queueLoading && puzzles.length === 0) {
        const emptyMessage = selectedPack === "all"
            ? "There are no reviewed Word Wheel puzzles left to publish."
            : selectedPack === WORD_WHEEL_SEASON_NONE
                ? "There are no reviewed puzzles with no pack left to publish."
                : `There are no reviewed puzzles in ${packLabel(selectedPack)} left to publish.`;
        return (
            <Box sx={{ width: "100%" }}>
                {toolbar}
                <Alert severity="success" sx={{ mb: 2 }}>
                    {emptyMessage}
                </Alert>
                <Button variant="contained" onClick={goToReviewedList} sx={{ minHeight: 44 }}>
                    Back to Reviewed
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            {toolbar}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(5, minmax(0, 1fr))",
                    },
                    alignItems: "stretch",
                }}
            >
                {puzzles.map((puzzle, index) => (
                    <QuickPublishPuzzleCard
                        key={puzzle.id}
                        puzzle={puzzle}
                        panelRef={(el) => {
                            panelRefs.current[index] = el;
                        }}
                        onRefresh={() => refreshPuzzle(puzzle.id)}
                        onNotify={notify}
                    />
                ))}
            </Box>

            <WordWheelPublishDialog
                open={openPublishDialog}
                onClose={() => setOpenPublishDialog(false)}
                onConfirm={handlePublish}
                templates={puzzles}
                language={puzzles[0]?.language || language || "all"}
                loading={loadingPublish}
            />

            <CustomSnackbar
                open={snackbarOpen}
                setOpen={setSnackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Box>
    );
}
